import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { HKPImage } from "../../../media/HKPImage";
import notifications from "../../../common/Components/Notifications/Notification";
import { canUseOrders } from "../../../helpers/orderEnabled";
import { useStudioPageChromeOverride } from "../../../common/StudioPageChromeContext";
import { getOrganisation } from "../../../Store/organisation/actions";
import {
  addSalesMemoLine,
  cancelSalesMemo,
  confirmSalesBilled,
  confirmSalesCredit,
  createSalesMemo,
  dropSalesMemoLine,
  getSalesMemo,
  keepSalesApprovalRest,
  reopenSalesMemo,
  returnSalesApprovalLines,
  sendSalesApproval,
  sendSalesMemo,
  setSalesMemoLineHsn,
  updateSalesMemo,
} from "../../../services/studioSalesApi";
import { unwrapGeoPayload } from "../../gi-registry/geoPath";
import AddParty from "../../organisations/AddParty";
import { visibleGst, type OrderCustomerOption } from "../../orders/orderParty";
import { SalesScanStage, useSalesScanner } from "../SalesScanPad";
import SalesPhonePicker from "./SalesPhonePicker";
import Sheet from "./SalesSheet";
import { useZohoMemo, zohoNextStep, ZohoMemoStatus, ZohoVoidSheet } from "./ZohoMemo";
import { ApprovalClosedPanel, ApprovalOutPanel, returnByLabel, useApprovalChallan } from "./ApprovalMemo";
import { ParcelsCard, canUseParcels, useMemoParcels } from "./ParcelsCard";
import MemoOrderLink from "./MemoOrderLink";
import { loadParties } from "../SalesPartyPicker";
import SalesMemoDocument from "../SalesMemoDocument";
import { formatInr, lineAmount, lineQty, lineRate, memoTotals, parseGstPct, standingMemoLines } from "../salesInvoice";
import { isInvoicedStatus } from "../salesMemoStatus";
import { partyFromConnect, useGstOptions } from "../salesPartyCreate";
import { groupBySourceInvoice, hasPhoto, initials, photoProps, phoneStatus, scanProblem, shortDate, type ScanProblem } from "./salesPhoneModel";
import "../../gi-registry/gi-registry.scss";
import "../../orders/orders.scss";
import "./salesPhone.scss";

const AddPartyModal: any = AddParty;
const GST_RATES = ["0", "5", "12", "18", "28"];
const SAVE_DELAY = 700;

type Party = { id: string; name: string; phone: string; gst: string; address: string };
type Toast = { ok: boolean; title: string; text: string; thumb?: string };

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const dataOf = (res: any) => unwrapGeoPayload(res) || res?.data;
/** HSN as typed (spaces dropped), when it is 4, 6 or 8 digits; otherwise "". */
export const cleanHsn = (value: string) => {
  const code = (value || "").replace(/\s/g, "");
  return /^(\d{4}|\d{6}|\d{8})$/.test(code) ? code : "";
};

const linePayload = (line: any) => ({
  id: line.id,
  proposedPrice: line.proposedPrice,
  qty: lineQty(line),
  discount: line.discount ?? 0,
  gstRate: line.gstRate || undefined,
  remarks: line.remarks,
  ownershipScope: line.ownershipScope || "PRODUCT",
});

const Thumb = ({ src, className }: { src?: string; className: string }) => {
  const photo = photoProps(src);
  return hasPhoto(photo) ? <HKPImage className={className} preset="THUMBNAIL" {...photo} alt="" loading="lazy" sizes="64px" />
    : <span className={className} />;
};

const SalesPhoneMemo = (props: any) => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const role = readRole();
  const canWrite = canUseOrders(role) && role !== "SUPER_ADMIN";
  const creditPath = location.pathname.indexOf("/credit") >= 0;
  const approvalPath = location.pathname.indexOf("/approval") >= 0;
  const wantsConfirm = new URLSearchParams(location.search).get("confirm") === "1";

  const [memo, setMemo] = useState<any>(null);
  const memoRef = useRef<any>(null);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "error" | "">("");
  const saveTimer = useRef<number | null>(null);
  const creating = useRef<Promise<string> | null>(null);
  const [party, setParty] = useState<Party | null>(null);
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyQ, setPartyQ] = useState("");
  const [parties, setParties] = useState<OrderCustomerOption[]>([]);
  const [partyLoading, setPartyLoading] = useState(false);
  const [addPartyOpen, setAddPartyOpen] = useState(false);
  const { gstOptions, handleOnInputChangeFilters } = useGstOptions(props.getOrganisation);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [toast, setToast] = useState<Toast | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [expanded, setExpanded] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [docNumber, setDocNumber] = useState("");
  const [problem, setProblem] = useState<(ScanProblem & { query: string }) | null>(null);
  const carried = useRef(new URLSearchParams(location.search).get("add") || "");
  const [menuOpen, setMenuOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [hsnEdit, setHsnEdit] = useState<{ lineId: string; value: string; saving?: boolean } | null>(null);
  /** Approval memo out with the buyer: lines ticked as come back. */
  const [returning, setReturning] = useState<Set<string>>(new Set());
  const [keepOpen, setKeepOpen] = useState(false);

  const isCredit = creditPath || memo?.type === "CREDIT";
  const isApproval = !isCredit && (approvalPath || memo?.type === "APPROVAL");
  const status = memo?.status || "DRAFT";
  const out = isApproval && status === "OUT_ON_APPROVAL";
  const closed = isApproval && status === "CLOSED";
  const draft = status === "DRAFT";
  const ready = status === "READY_FOR_BILLING";
  const done = isInvoicedStatus(status);
  const editable = draft && canWrite;
  const memoId = memo?.id || "";

  const applyMemo = useCallback((data: any) => {
    if (!data) return;
    memoRef.current = data;
    setMemo(data);
    // A piece someone else marked returned meanwhile drops out of the ticked set.
    const stillOut = new Set((data.lines || []).filter((line: any) => line.lineStatus === "ACTIVE").map((line: any) => line.id));
    setReturning((cur) => (Array.from(cur).every((id) => stillOut.has(id)) ? cur : new Set(Array.from(cur).filter((id) => stillOut.has(id)))));
    if (data.partyOrganizationId || data.partyName) {
      setParty({
        id: data.partyOrganizationId || "",
        name: data.partyName || "",
        phone: data.partyPhone || "",
        gst: visibleGst(data.partyGstin),
        address: String(data.partyAddress || "").trim(),
      });
    }
  }, []);

  useEffect(() => {
    if (!canUseOrders(role)) history.replace("/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNew || id === memoRef.current?.id) {
      if (isNew && !creditPath && canWrite) setPartyOpen(true);
      return;
    }
    let live = true;
    setLoading(true);
    setLoadError("");
    getSalesMemo(id).then((res: any) => {
      if (!live) return;
      if (!res?.success) {
        setLoadError(res?.message || "Could not load this memo");
        return;
      }
      const data = dataOf(res);
      applyMemo(data);
      if (wantsConfirm && data?.status === "READY_FOR_BILLING") setConfirmOpen(true);
    }).catch(() => live && setLoadError("Could not load this memo. Check your connection."))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const reloadMemo = useCallback(async () => {
    if (!memoRef.current?.id) return;
    const res: any = await getSalesMemo(memoRef.current.id);
    if (res?.success) applyMemo(dataOf(res));
  }, [applyMemo]);
  const goToSales = useCallback(() => history.push("/sales"), [history]);
  const zoho = useZohoMemo({
    memo, role, active: ready && !isCredit && !isApproval && canWrite, reload: reloadMemo, onCancelled: goToSales,
  });
  const challan = useApprovalChallan({ memo, role, active: (out || closed) && canWrite, reload: reloadMemo });
  const parcels = useMemoParcels({ memo: isCredit ? null : memo, role });

  const lines = useMemo(() => standingMemoLines(memo?.lines || []), [memo]);
  const active = useMemo(() => lines.filter((line: any) => line.lineStatus === "ACTIVE"), [lines]);
  const totals = memoTotals(lines);

  /** One memo per screen: concurrent scans wait on the same create. */
  const ensureMemo = async (): Promise<string> => {
    if (memoRef.current?.id) return memoRef.current.id;
    if (!creating.current) {
      creating.current = (async () => {
        const res: any = await createSalesMemo({
          type: isCredit ? "CREDIT" : isApproval ? "APPROVAL" : "SALE",
          partyOrganizationId: !isCredit && party?.id ? party.id : undefined,
          partyName: !isCredit && party?.name ? party.name : undefined,
          partyPhone: !isCredit && party?.phone ? party.phone : undefined,
        });
        if (!res?.success) throw new Error(res?.message || "Could not start the memo");
        const data = dataOf(res);
        applyMemo(data);
        // Address bar only: a router replace remounts this screen and would drop the open scanner.
        window.history.replaceState(window.history.state, "", `/sales/memos/${data.id}`);
        return data.id as string;
      })().finally(() => { creating.current = null; });
    }
    return creating.current;
  };

  const saveNow = async (current = memoRef.current) => {
    if (!current?.id || current.type === "CREDIT" || current.status !== "DRAFT") return true;
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setSaveState("saving");
    try {
      const res: any = await updateSalesMemo(current.id, {
        partyOrganizationId: current.partyOrganizationId || undefined,
        lines: (current.lines || []).filter((line: any) => line.lineStatus === "ACTIVE").map(linePayload),
      });
      if (!res?.success) {
        setSaveState("error");
        notifications({ title: "Not saved", message: res?.message || "Try again", type: "danger" });
        return false;
      }
      setSaveState("saved");
      return true;
    } catch {
      setSaveState("error");
      return false;
    }
  };

  const patchLine = (lineId: string, patch: Record<string, unknown>) => {
    const next = { ...memoRef.current, lines: (memoRef.current?.lines || []).map((line: any) => (line.id === lineId ? { ...line, ...patch } : line)) };
    memoRef.current = next;
    setMemo(next);
    setSaveState("saving");
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => void saveNow(), SAVE_DELAY);
  };

  useEffect(() => () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); }, []);

  const saveHsn = async () => {
    const edit = hsnEdit;
    if (!edit || edit.saving || !memoId) return;
    const code = cleanHsn(edit.value);
    if (!code) {
      notifications({ title: "HSN not saved", message: "Type the HSN code as 4, 6 or 8 digits.", type: "warning" });
      return;
    }
    const setSaving = (saving: boolean) => setHsnEdit((cur) => (cur && cur.lineId === edit.lineId ? { ...cur, saving } : cur));
    setSaving(true);
    try {
      // A waiting price or GST change goes first, so the two saves never write the memo at the same moment.
      if (saveTimer.current && !(await saveNow())) {
        setSaving(false);
        notifications({ title: "HSN not saved", message: "Your other changes did not save. Try again.", type: "danger" });
        return;
      }
      const res: any = await setSalesMemoLineHsn(memoId, edit.lineId, code);
      if (!res?.success) {
        setSaving(false);
        notifications({ title: "HSN not saved", message: res?.message || "Try again", type: "danger" });
        // The memo may have moved on (a Zoho draft started elsewhere): show it as it is now.
        void reloadMemo();
        return;
      }
    } catch {
      setSaving(false);
      notifications({ title: "HSN not saved", message: "No connection. Check your internet and try again.", type: "danger" });
      return;
    }
    // Only this line's HSN changes here, so an unsaved price or GST edit on the screen is kept.
    const next = { ...memoRef.current, lines: (memoRef.current?.lines || []).map((line: any) => (line.id === edit.lineId ? { ...line, hsnCode: code } : line)) };
    memoRef.current = next;
    setMemo(next);
    setHsnEdit(null);
  };

  const pickParty = async (next: Party) => {
    setParty(next);
    setPartyOpen(false);
    const current = memoRef.current;
    if (!current?.id) return;
    const updated = { ...current, partyOrganizationId: next.id, partyName: next.name };
    memoRef.current = updated;
    setMemo(updated);
    const res: any = await updateSalesMemo(current.id, {
      partyOrganizationId: next.id,
      lines: (current.lines || []).filter((line: any) => line.lineStatus === "ACTIVE").map(linePayload),
    });
    if (res?.success) applyMemo(dataOf(res));
    else notifications({ title: "Party not saved", message: res?.message || "Try again", type: "danger" });
  };

  useEffect(() => {
    if (!partyOpen) return;
    let live = true;
    setPartyLoading(true);
    const wait = window.setTimeout(() => {
      loadParties(partyQ.trim(), 0)
        .then((page) => live && setParties(page.parties))
        .catch(() => live && setParties([]))
        .finally(() => live && setPartyLoading(false));
    }, partyQ ? 300 : 0);
    return () => { live = false; window.clearTimeout(wait); };
  }, [partyOpen, partyQ]);

  /**
   * Scan, NFC, USB reader and the Add pieces grid all land here. Returns "" when added, else the reason
   * (the grid shows it on the tile; scans show it in the scanner toast).
   */
  const addPiece = async (query: string, mode: "scan" | "browse" | "carry"): Promise<string> => {
    const q = String(query || "").trim();
    if (!q || !canWrite) return "Not allowed";
    setBusy(true);
    try {
      const target = await ensureMemo();
      await saveNow();
      const res: any = await addSalesMemoLine(target, q);
      if (!res?.success && mode === "scan" && res?.message === "This piece is already on this memo") {
        // The camera saw the same tag again; it is already here, so this is not a failure.
        setToast({ ok: true, title: "Already on this " + (isCredit ? "credit note" : "memo"), text: "Scan the next piece." });
        return "";
      }
      if (!res?.success) {
        const found = scanProblem(res?.message, isCredit);
        if (found.kind === "text") {
          if (mode === "scan") setToast({ ok: false, title: "Not added", text: found.text });
          if (mode === "carry") notifications({ title: "Not added", message: found.text, type: "danger" });
          return found.text;
        }
        closeScan();
        setPickOpen(false);
        setProblem({ ...found, query: q });
        return found.kind === "noInvoice" ? "No HKP invoice for this piece" : "Sold to another party";
      }
      const data = dataOf(res);
      const before = new Set((memoRef.current?.lines || []).map((line: any) => line.id));
      applyMemo(data);
      const added = (data?.lines || []).find((line: any) => !before.has(line.id)) || {};
      if (mode === "scan") {
        setScanCount((count) => count + 1);
        setToast({ ok: true, title: "Added", text: `${added.name || "Piece"} · ${formatInr(lineRate(added))}`, thumb: added.thumbnail });
      }
      return "";
    } catch (err: any) {
      const message = err?.message || "Check your connection and try again";
      if (mode !== "browse") notifications({ title: "Not added", message, type: "danger" });
      return message;
    } finally {
      setBusy(false);
    }
  };

  // A piece handed over from "New credit note" on another party's credit note.
  useEffect(() => {
    const q = carried.current;
    carried.current = "";
    if (q && isNew && creditPath && canWrite) void addPiece(q, "carry");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanner = useSalesScanner((value) => addPiece(value, "scan"), busy || !editable, true);

  const openScan = () => {
    setToast(null);
    setScanCount(0);
    setScanOpen(true);
    scanner.startCamera();
  };

  function closeScan() {
    scanner.stopCamera();
    scanner.stopNfc();
    setScanOpen(false);
  }


  const dropLine = async (lineId: string) => {
    if (!memoId) return;
    await saveNow();
    const res: any = await dropSalesMemoLine(memoId, lineId);
    if (!res?.success) {
      notifications({ title: "Not removed", message: res?.message || "Try again", type: "danger" });
      return;
    }
    applyMemo(dataOf(res));
  };

  const markReady = async () => {
    if (!isCredit && !party?.id) {
      setPartyOpen(true);
      return;
    }
    if (!memoId || !active.length) return;
    setBusy(true);
    try {
      if (!(await saveNow())) return;
      const res: any = isApproval ? await sendSalesApproval(memoId) : await sendSalesMemo(memoId);
      if (!res?.success) {
        notifications({ title: isApproval ? "Not sent yet" : "Not ready yet", message: res?.message || "Check party and pieces", type: "danger" });
        return;
      }
      applyMemo(dataOf(res));
      window.scrollTo(0, 0);
    } catch {
      notifications({ title: "Not ready yet", message: "Check your connection and try again", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      const res: any = await reopenSalesMemo(memoId);
      if (!res?.success) {
        notifications({ title: "Could not edit", message: res?.message || "This memo stays locked", type: "danger" });
        return;
      }
      applyMemo(dataOf(res));
    } finally {
      setBusy(false);
    }
  };

  const saveReturnBy = async (date: string) => {
    if (!memoId) return false;
    try {
      const res: any = await updateSalesMemo(memoId, { returnBy: date });
      if (!res?.success) {
        notifications({ title: "Date not saved", message: res?.message || "Try again", type: "danger" });
        return false;
      }
      applyMemo(dataOf(res));
      return true;
    } catch {
      notifications({ title: "Date not saved", message: "Check your connection and try again", type: "danger" });
      return false;
    }
  };

  const markReturned = async () => {
    const ids = Array.from(returning);
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      const res: any = await returnSalesApprovalLines(memoId, ids);
      if (!res?.success) {
        notifications({ title: "Not marked returned", message: res?.message || "Try again", type: "danger" });
        void reloadMemo();
        return;
      }
      const data = dataOf(res);
      applyMemo(data);
      setReturning(new Set());
      notifications({ title: "Back in stock", message: `${ids.length} ${ids.length === 1 ? "piece" : "pieces"} marked returned.`, type: "success" });
      // HKP holds the return now; Zoho's challan follows (a failure there keeps a "Send returns to Zoho" link).
      if (challan.on && data?.zohoChallanNumber && (Number(data?.zohoReturnsPending) > 0 || (data?.status === "CLOSED" && !data?.zohoChallanSettled))) {
        void challan.syncReturns();
      }
    } catch {
      notifications({ title: "Not marked returned", message: "Check your connection and try again", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const keepRest = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res: any = await keepSalesApprovalRest(memoId);
      if (!res?.success) {
        notifications({ title: "No sale memo yet", message: res?.message || "Try again", type: "danger" });
        void reloadMemo();
        return;
      }
      const sale = dataOf(res);
      setKeepOpen(false);
      // The approval is closed: its Zoho challan is closed as delivered (a failure leaves a link on the approval memo).
      if (challan.on && memoRef.current?.zohoChallanNumber) await challan.syncReturns();
      notifications({ title: `Sale memo ${sale?.memoCode || ""}`.trim(), message: "Check the prices, then bill it as usual.", type: "success" });
      history.push(`/sales/memos/${sale.id}`);
    } catch {
      notifications({ title: "No sale memo yet", message: "Check your connection and try again", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const confirmDoc = async () => {
    const number = docNumber.trim();
    if (!number || busy) return;
    setBusy(true);
    try {
      const body = { provider: "MANUAL", documentNumber: number, confirmed: true };
      const res: any = isCredit ? await confirmSalesCredit(memoId, body) : await confirmSalesBilled(memoId, body);
      if (!res?.success) {
        notifications({ title: "Not confirmed", message: res?.message || "Try again", type: "danger" });
        return;
      }
      applyMemo(dataOf(res));
      setConfirmOpen(false);
      setDocNumber("");
      window.scrollTo(0, 0);
    } catch {
      notifications({ title: "Not confirmed", message: "Check your connection and try again", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const cancelMemo = async () => {
    setMenuOpen(false);
    if (!memoId) {
      history.push("/sales");
      return;
    }
    if (!window.confirm(`Cancel ${memo?.memoCode || (isApproval ? "this approval memo" : "this memo")}? Its pieces go back to stock.`)) return;
    const res: any = await cancelSalesMemo(memoId, "NOT_BILLED");
    if (!res?.success) {
      notifications({ title: "Not cancelled", message: res?.message || "Try again", type: "danger" });
      return;
    }
    history.push("/sales");
  };

  const back = async () => {
    await saveNow();
    history.push("/sales");
  };

  useStudioPageChromeOverride({
    title: `${isCredit ? "Credit note" : isApproval ? "On approval" : "Memo"}${memo?.memoCode ? ` ${memo.memoCode}` : ""}`,
    onBack: () => void back(),
  });

  if (loading) return <div className="sp"><p className="t13 sp-empty" role="status">Loading memo…</p></div>;
  if (loadError) {
    return (
      <div className="sp">
        <div className="sp-warn" role="alert">{loadError}</div>
        <button type="button" className="sp-btn" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const st = phoneStatus(status, isCredit ? "CREDIT" : isApproval ? "APPROVAL" : "SALE");
  const outLines = lines.filter((line: any) => line.lineStatus === "ACTIVE");
  const docLabel = isCredit ? "Credit note number" : "Invoice number";
  const pieces = `${lines.length} ${lines.length === 1 ? "piece" : "pieces"}`;
  const sourceInvoices = Array.from(new Set(active.map((line: any) => line.sourceInvoiceNumber).filter(Boolean)));

  // HSN can be typed on the line until a Zoho draft exists: Draft or Ready for Billing, sales only.
  // Approval memos: until the Zoho delivery challan is made (it carries the HSN).
  const hsnOpen = !isCredit && canWrite && (isApproval
    ? (draft || out) && !memo?.zohoChallanNumber && !challan.busy
    : (draft || ready) && !zoho.held);
  // One HSN box at a time, so typing on one piece is never dropped by opening another.
  const hsnCanStart = hsnOpen && !hsnEdit;
  const hsnRow = (line: any) => {
    const edit = hsnEdit;
    if (edit && edit.lineId === line.id) {
      return (
        <div className="sp-row sp-hsn">
          <input className="sp-hsn__input" inputMode="numeric" autoFocus maxLength={11} placeholder="8 digits"
            aria-label={`HSN code for ${line.name || "piece"}`} value={edit.value}
            onChange={(e) => setHsnEdit({ ...edit, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") void saveHsn();
              if (e.key === "Escape") setHsnEdit(null);
            }} />
          <button type="button" className="sp-hsn__save" disabled={!!edit.saving} onClick={() => void saveHsn()}>
            {edit.saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="sp-link sp-hsn__link" onClick={() => setHsnEdit(null)}>Cancel</button>
        </div>
      );
    }
    if (line.hsnCode) {
      return (
        <div className="sp-row sp-hsn">
          <span className="t13">HSN {line.hsnCode}</span>
          {hsnCanStart ? (
            <button type="button" className="sp-link sp-hsn__link" onClick={() => setHsnEdit({ lineId: line.id, value: line.hsnCode })}>Change</button>
          ) : null}
        </div>
      );
    }
    return hsnOpen ? (
      <div className="sp-row sp-hsn">
        <span className="t13 sp-hsn__missing grow" title={isApproval ? "Needed for the Zoho delivery challan" : "Needed for the invoice"}>No HSN yet</span>
        {hsnCanStart ? (
          <button type="button" className="sp-link sp-hsn__link" onClick={() => setHsnEdit({ lineId: line.id, value: "" })}>+ Add HSN</button>
        ) : null}
      </div>
    ) : null;
  };

  const lineCard = (line: any) => {
    const open = expanded === line.id;
    return (
      <div key={line.id} className={`sp-line${isApproval && line.lineStatus !== "ACTIVE" && !draft ? " is-settled" : ""}`}>
        {out && canWrite && line.lineStatus === "ACTIVE" ? (
          <input type="checkbox" className="sp-came-back" checked={returning.has(line.id)}
            aria-label={`${line.name || "Piece"} came back`}
            onChange={(e) => setReturning((cur) => {
              const next = new Set(cur);
              if (e.target.checked) next.add(line.id); else next.delete(line.id);
              return next;
            })} />
        ) : null}
        <Thumb src={line.thumbnail} className="sp-ph" />
        <div className="sp-line__body">
          <div className="sp-row">
            <div className="t16 grow">{line.name || "Untitled piece"}</div>
            {isApproval && !draft ? (
              <span className={`sp-st is-${line.lineStatus === "ACTIVE" ? "ready" : line.lineStatus === "KEPT" ? "done" : "closed"}`}>
                {line.lineStatus === "ACTIVE" ? "Out" : line.lineStatus === "KEPT" ? "Kept" : "Returned"}
              </span>
            ) : null}
            {editable ? (
              <button type="button" className="sp-x" aria-label={`Remove ${line.name || "piece"}`} onClick={() => dropLine(line.id)}>×</button>
            ) : null}
          </div>
          {isCredit ? (
            <div className="t13">Billed {formatInr(lineAmount(line))}{line.gstRate ? ` · GST ${parseGstPct(line.gstRate)}%` : ""}</div>
          ) : editable ? (
            <>
              <div className="sp-row">
                <input className="sp-price" type="number" inputMode="decimal" min={0} aria-label="Rate"
                  value={line.proposedPrice ?? ""} onChange={(e) => patchLine(line.id, { proposedPrice: e.target.value === "" ? null : Number(e.target.value) })} />
                <button type="button" className="sp-toggle is-field grow" aria-expanded={open}
                  aria-label={`GST, quantity and discount for ${line.name || "piece"}`}
                  onClick={() => setExpanded(open ? "" : line.id)}>
                  <span className="clip">
                    {line.gstRate ? `GST ${parseGstPct(line.gstRate)}%` : "No GST"}
                    {Number(line.discount) > 0 ? ` · ${line.discount}% off` : ""}
                    {lineQty(line) > 1 ? ` · ×${lineQty(line)}` : ""}
                  </span>
                  <span className="sp-toggle__chev" aria-hidden="true">{open ? "▴" : "▾"}</span>
                </button>
              </div>
              {open ? (
                <div className="sp-more-grid">
                  <label>Qty
                    <input type="number" inputMode="numeric" min={1} value={line.qty ?? 1}
                      onChange={(e) => patchLine(line.id, { qty: Math.max(1, Number(e.target.value) || 1) })} />
                  </label>
                  <label>Discount %
                    <input type="number" inputMode="decimal" min={0} max={100} value={line.discount ?? 0}
                      onChange={(e) => patchLine(line.id, { discount: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })} />
                  </label>
                  <label>GST
                    <select value={line.gstRate ? String(parseGstPct(line.gstRate)) : ""} onChange={(e) => patchLine(line.id, { gstRate: e.target.value })}>
                      <option value="">None</option>
                      {GST_RATES.map((rate) => <option key={rate} value={rate}>{rate}%</option>)}
                    </select>
                  </label>
                  <label>Hand over
                    <select value={line.ownershipScope || "PRODUCT"} onChange={(e) => patchLine(line.id, { ownershipScope: e.target.value })}>
                      <option value="PRODUCT">Piece</option>
                      <option value="PRODUCT_AND_DESIGN">Piece + design</option>
                    </select>
                  </label>
                </div>
              ) : null}
            </>
          ) : (
            <div className="sp-row">
              <span className="t13 grow">
                {lineQty(line)} × {formatInr(lineRate(line))}
                {Number(line.discount) > 0 ? ` · ${line.discount}% off` : ""}
                {line.gstRate ? ` · GST ${parseGstPct(line.gstRate)}%` : ""}
              </span>
              <span className="sp-amt">{formatInr(lineAmount(line))}</span>
            </div>
          )}
          {!isCredit && (!isApproval || line.lineStatus === "ACTIVE") ? hsnRow(line) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="sp">
      <div className="sp-head">
        <div className="grow">
          <div className="t13">
            {draft ? <>Draft{saveState === "saving" ? " · saving…" : saveState === "saved" ? <> · <span className="sp-saved">saved</span></> : saveState === "error" ? " · not saved" : ""}</>
              : ""}
          </div>
        </div>
        {draft ? null : <span className={`sp-st is-${st.tone}`}>{st.label}</span>}
        {memoId ? <button type="button" className="sp-more" aria-label="More actions" onClick={() => setMenuOpen(true)}>⋯</button> : null}
      </div>

      {done ? (
        <>
          <div className="sp-av" style={{ width: 72, height: 72, margin: "21px auto 0", background: "#e8f7ee", color: "#1e7b34", fontSize: 34 }}>✓</div>
          <h2 style={{ fontSize: 26, fontWeight: 650, textAlign: "center", margin: 0 }}>
            {isCredit ? "Credit note" : "Invoice"} {memo?.creditNoteNumber || memo?.invoiceNumber}
          </h2>
          <p className="t13" style={{ textAlign: "center", margin: 0 }}>
            {party?.name} · {formatInr(totals.total)}{memo?.billedAt ? ` · ${shortDate(memo.billedAt)}` : ""}
          </p>
          <div className="sp-will">
            <div><span className="ok">✓</span>{pieces} {isCredit ? "back in stock" : "out of stock"}</div>
            <div><span className="ok">✓</span>{isCredit ? "Ownership returned to your studio" : `Ownership moved to ${party?.name || "the party"}`}</div>
          </div>
        </>
      ) : null}
      {!isApproval && !isCredit ? <ParcelsCard memo={memo} parcels={parcels} partyPhone={party?.phone} /> : null}

      {!done && (party || !isCredit) ? (
        <div className="sp-party">
          <div className="sp-av">{party?.name ? initials(party.name) : "?"}</div>
          <div className="grow">
            <div className="t16 clip">{party?.name || (isApproval ? "Who gets the pieces?" : "Who is buying?")}</div>
            <div className="t13 clip">
              {party?.name ? [party.gst ? `GST ${party.gst}` : "", party.phone].filter(Boolean).join(" · ") || (isCredit ? "Set from the first returned piece" : "")
                : isApproval ? "Pick the party before sending" : "Pick the party before billing"}
            </div>
          </div>
          {editable && !isCredit ? (
            <button type="button" className="sp-link" onClick={() => setPartyOpen(true)}>{party?.name ? "Change" : "Pick"}</button>
          ) : null}
        </div>
      ) : null}

      {!isCredit ? (
        // Shown once the parcels call proved the feature is on, or when the memo is already linked.
        <MemoOrderLink memo={memo} canEdit={canUseParcels(role)}
          shown={parcels.status === "ready" || parcels.status === "error" || !!memo?.studioOrderId}
          onLinked={(view) => setMemo((cur: any) => (cur && cur.id === view.memoId
            ? { ...cur, studioOrderId: view.studioOrderId, studioOrderCode: view.studioOrderCode } : cur))} />
      ) : null}

      {ready ? (
        <>
          <div className="sp-info">
            <b>Next:</b>{" "}
            {isCredit
              ? `make the credit note in your billing software${sourceInvoices.length ? ` against ${sourceInvoices.join(", ")}` : ""}. Then add its number here.`
              : zohoNextStep(memo, zoho)
              || "make the tax invoice in your billing software using this memo. Then add its number here. Pieces stay reserved for this party until then."}
          </div>
          <ZohoMemoStatus memo={memo} zoho={zoho} />
          <div className="sp-btns">
            <button type="button" className="sp-btn" onClick={() => setPreview(true)}>⎙ Share / print</button>
            {canWrite ? (
              <button type="button" className="sp-btn" disabled={busy || zoho.busy || zoho.pushing}
                onClick={zoho.hasDraft ? () => zoho.openVoid("reopen") : reopen}>Edit</button>
            ) : null}
          </div>
        </>
      ) : null}

      {out ? (
        <ApprovalOutPanel memo={memo} canWrite={canWrite} challan={challan} onReturnBy={saveReturnBy} />
      ) : null}
      {closed ? <ApprovalClosedPanel memo={memo} challan={challan} onOpenSale={(saleId) => history.push(`/sales/memos/${saleId}`)} /> : null}
      {isApproval ? <ParcelsCard memo={memo} parcels={parcels} partyPhone={party?.phone} /> : null}
      {isApproval && draft && memo?.returnBy ? (
        <div className="sp-row sp-hsn">
          <span className="t13 grow">Return or confirm by <b>{returnByLabel(memo.returnBy)}</b> (15 days; change it after sending)</span>
        </div>
      ) : null}
      {!isApproval && memo?.approvalMemoId ? (
        <div className="sp-info">From approval memo <button type="button" className="sp-link"
          onClick={() => history.push(`/sales/memos/${memo.approvalMemoId}`)}><span className="mono">{memo.approvalMemoCode}</span></button>: the buyer kept these pieces.</div>
      ) : null}

      {editable ? (
        lines.length === 0 ? (
          <>
            <button type="button" className="sp-scanbig" onClick={openScan} disabled={busy}>
              <span style={{ fontSize: 34, opacity: 1 }} aria-hidden="true">⌗</span>
              <b>{isCredit ? "Scan a returned piece" : "Scan QR or tap NFC tag"}</b>
              <span>{isCredit
                ? "We find its invoice and party for you. Pieces can come from different invoices."
                : "Keep scanning. Each piece adds itself with photo and price."}</span>
            </button>
            <div className="sp-or">or</div>
            <button type="button" className="sp-btn" onClick={() => setPickOpen(true)}>
              ▦ {isCredit ? "Browse sold pieces" : "Browse pieces"}
            </button>
          </>
        ) : (
          <div className="sp-btns">
            <button type="button" className="sp-btn is-pri" onClick={openScan} disabled={busy}>⌗ Scan</button>
            <button type="button" className="sp-btn" onClick={() => setPickOpen(true)}>+ Add pieces</button>
          </div>
        )
      ) : null}

      {isCredit
        ? groupBySourceInvoice(lines as any[]).map((group) => (
          <React.Fragment key={`${group.invoice}-${group.memoCode}`}>
            <div className="sp-grp"><span>From {group.invoice || "invoice"}</span><span className="mono">{group.memoCode}</span></div>
            {group.lines.map(lineCard)}
          </React.Fragment>
        ))
        : lines.map(lineCard)}

      {editable && lines.length === 0 ? <p className="t13 sp-empty">No pieces yet.</p> : null}

      {canWrite && (draft || ready) ? (
        <div className="sp-bar">
          <div className="sp-tot">
            <span className="t13">
              {pieces}
              {isCredit && sourceInvoices.length > 1 ? ` · ${sourceInvoices.length} invoices` : ""}
              {totals.hasGst ? ` · incl. GST ${formatInr(totals.gst)}` : ""}
            </span>
            <b>{formatInr(totals.total)}</b>
          </div>
          {draft ? (
            <button type="button" className="sp-btn is-pri" disabled={busy || !active.length} onClick={markReady}>
              {isCredit ? "Ready for credit note" : isApproval ? (party?.id || !active.length ? "Send on approval" : "Pick party, then send")
                : party?.id || !active.length ? "Ready for billing" : "Pick party, then bill"}
            </button>
          ) : zoho.on && !zoho.pushing ? (
            <button type="button" className="sp-btn is-pri" disabled={busy || zoho.busy}
              onClick={zoho.hasDraft ? zoho.check : zoho.push}>
              {zoho.busy ? "Talking to Zoho…" : zoho.hasDraft ? "Check Zoho" : "Create Zoho draft"}
            </button>
          ) : zoho.checking || zoho.held || zoho.statusFailed ? (
            <button type="button" className="sp-btn is-pri" disabled>
              {zoho.checking ? "Checking Zoho…" : zoho.pushing ? "Sending to Zoho…" : zoho.held ? "Draft in Zoho" : "Zoho not checked"}
            </button>
          ) : (
            <button type="button" className="sp-btn is-pri" disabled={busy} onClick={() => setConfirmOpen(true)}>
              Add {isCredit ? "credit note" : "invoice"} number
            </button>
          )}
        </div>
      ) : null}
      {out && canWrite ? (
        <div className="sp-bar">
          <div className="sp-tot">
            <span className="t13">{outLines.length} still out{memo?.overdue ? " · overdue" : ""}</span>
            <b>{formatInr(memoTotals(outLines).total)}</b>
          </div>
          {returning.size ? (
            <button type="button" className="sp-btn is-pri" disabled={busy || challan.busy} onClick={markReturned}>
              {busy ? "Saving…" : `Mark returned (${returning.size})`}
            </button>
          ) : (
            <button type="button" className="sp-btn is-pri" disabled={busy || challan.busy || !outLines.length} onClick={() => setKeepOpen(true)}>
              Keep the rest · bill
            </button>
          )}
        </div>
      ) : null}
      {closed ? (
        <div className="sp-bar">
          <div className="sp-btns">
            <button type="button" className="sp-btn" onClick={() => history.push("/sales")}>All sales</button>
            {memo?.saleMemoId ? (
              <button type="button" className="sp-btn is-pri" onClick={() => history.push(`/sales/memos/${memo.saleMemoId}`)}>Open sale memo</button>
            ) : canWrite ? (
              <button type="button" className="sp-btn is-pri" onClick={() => history.push("/sales/approval/new")}>+ New approval</button>
            ) : null}
          </div>
        </div>
      ) : null}
      {keepOpen ? (
        <Sheet label="Keep the rest" onClose={() => !busy && setKeepOpen(false)}>
          <h2>{memo?.partyName || "The buyer"} keeps {outLines.length} {outLines.length === 1 ? "piece" : "pieces"}</h2>
          <div className="sp-will">
            <div className="t16">When you confirm</div>
            <div><span className="ok">✓</span>A sale memo for {memo?.partyName || "the buyer"} is made with these pieces and prices</div>
            <div><span className="ok">✓</span>You check it and bill it as usual{challan.number ? " (Zoho invoice from there)" : ""}</div>
            <div><span className="ok">✓</span>This approval memo closes</div>
          </div>
          {returning.size ? <p className="t13" style={{ margin: 0 }}>Ticked pieces are not returned yet. Mark them returned first.</p> : null}
          <button type="button" className="sp-btn is-pri" disabled={busy || returning.size > 0} onClick={keepRest}>
            {busy ? "Making sale memo…" : `Make sale memo · ${formatInr(memoTotals(outLines).total)}`}
          </button>
        </Sheet>
      ) : null}
      {done ? (
        <div className="sp-bar">
          <div className="sp-btns">
            <button type="button" className="sp-btn" onClick={() => history.push("/sales")}>All sales</button>
            {canWrite ? (
              <button type="button" className="sp-btn is-pri" onClick={() => history.push(isCredit ? "/sales/credit/new" : "/sales/memos/new")}>
                + New {isCredit ? "credit note" : "memo"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {scanOpen ? (
        <div className="sp-scan" role="dialog" aria-modal="true" aria-label="Scan pieces">
          <div className="sp-scan__head">
            <button type="button" className="sp-back" aria-label="Close scanner" onClick={closeScan}>✕</button>
            <h2>{isCredit ? "Scan returned pieces" : "Scan pieces"}</h2>
            <span className="t16" style={{ color: "#fff" }}>{scanCount} added</span>
          </div>
          <p className="sp-scan__hint">{busy ? "Adding…" : "Point at the QR on the tag"}</p>
          <div className="sp-scan__stage">
            <SalesScanStage scan={scanner} />
            {toast ? (
              <div className="sp-toast" role="status">
                <div className="sp-row">
                  {toast.thumb ? <Thumb src={toast.thumb} className="sp-th" /> : null}
                  <div className="grow">
                    <div className={toast.ok ? "ok" : "bad"}>{toast.ok ? "✓ " : ""}{toast.title}</div>
                    <div className="t16" style={{ fontWeight: toast.ok ? 650 : 400 }}>{toast.text}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="sp-nfc">
            <button type="button" className="sp-ring" onClick={scanner.startNfc} aria-label="Read NFC tag">NFC</button>
            <div className="grow">
              <div className="t16">{scanner.nfcOn ? "Hold the phone to the tag" : "Tap NFC to read a tag"}</div>
              <div className="t13">A USB NFC reader also works: tap NFC, then scan.</div>
            </div>
          </div>
          <div className="sp-scan__bar">
            <button type="button" className="sp-btn is-pri" style={{ width: "100%" }} onClick={closeScan}>
              Done · back to {isCredit ? "credit note" : "memo"} ({lines.length})
            </button>
          </div>
        </div>
      ) : null}

      {pickOpen ? (
        <SalesPhonePicker
          credit={isCredit}
          addedIds={new Set(active.map((line: any) => String(line.productUniqueId)))}
          count={lines.length}
          totalLabel={formatInr(totals.total)}
          onAdd={(product) => addPiece(product.productUniqueId, "browse")}
          onRemove={async (productUniqueId) => {
            const line = active.find((row: any) => row.productUniqueId === productUniqueId);
            if (line) await dropLine(String(line.id));
          }}
          onScan={() => { setPickOpen(false); openScan(); }}
          onClose={() => setPickOpen(false)}
        />
      ) : null}

      {partyOpen ? (
        <Sheet label="Pick party" onClose={() => setPartyOpen(false)}>
          <h2>Who is buying?</h2>
          <input className="sp-input" type="search" value={partyQ} placeholder="Name, phone or GST" aria-label="Search party"
            onChange={(e) => setPartyQ(e.target.value)} />
          {partyLoading ? <p className="t13" role="status">Loading parties…</p> : null}
          <div className="sp-results">
            {parties.map((row) => (
              <button key={row.partyOrganizationId} type="button" className="sp-result"
                onClick={() => pickParty({
                  id: row.partyOrganizationId, name: row.customerName, phone: row.customerPhone,
                  gst: row.customerGst, address: row.customerAddress,
                })}>
                <div className="sp-av">{initials(row.customerName)}</div>
                <div className="grow">
                  <div className="t16 clip">{row.customerName}</div>
                  <div className="t13 clip">{[row.customerPhone, row.customerGst ? `GST ${row.customerGst}` : ""].filter(Boolean).join(" · ")}</div>
                </div>
              </button>
            ))}
            {!partyLoading && parties.length === 0 ? <p className="t13">No party found.</p> : null}
          </div>
          <button type="button" className="sp-link" style={{ textAlign: "left" }} onClick={() => setAddPartyOpen(true)}>+ Add new party</button>
          <p className="t13" style={{ margin: 0 }}>You can also scan first and pick the party before billing.</p>
        </Sheet>
      ) : null}

      {confirmOpen && !zoho.held ? (
        <Sheet label={docLabel} onClose={() => !busy && setConfirmOpen(false)}>
          <h2>{docLabel}</h2>
          <input className="sp-input" autoFocus value={docNumber} placeholder={isCredit ? "CN-2026-0012" : "INV-2026-0233"}
            aria-label={docLabel} onChange={(e) => setDocNumber(e.target.value)} />
          <p className="t13" style={{ margin: 0 }}>
            As printed on the {isCredit ? "credit note" : "tax invoice"} in your billing software.
            {isCredit && sourceInvoices.length ? ` It covers ${sourceInvoices.join(" and ")}.` : ""}
          </p>
          <div className="sp-will">
            <div className="t16">When you confirm</div>
            {isCredit ? (
              <>
                <div><span className="ok">✓</span>{pieces} come back into stock</div>
                <div><span className="ok">✓</span>Ownership returns to your studio</div>
                <div><span className="ok">✓</span>Each invoice shows the piece as returned</div>
              </>
            ) : (
              <>
                <div><span className="ok">✓</span>{pieces} leave stock</div>
                <div><span className="ok">✓</span>Ownership moves to <b>{party?.name}</b></div>
                <div><span className="ok">✓</span>Their certificates show the new owner</div>
              </>
            )}
          </div>
          {!isCredit ? <p className="t13" style={{ margin: 0 }}>Wrong later? Make a credit note to bring pieces back.</p> : null}
          <button type="button" className="sp-btn is-pri" disabled={busy || !docNumber.trim()} onClick={confirmDoc}>
            {busy ? "Confirming…" : `Confirm ${isCredit ? "credit note" : "invoice"} · ${formatInr(totals.total)}`}
          </button>
        </Sheet>
      ) : null}

      {problem ? (
        <Sheet label="Piece not added" onClose={() => setProblem(null)}>
          {problem.kind === "noInvoice" ? (
            <>
              <h2>No invoice found for this piece</h2>
              <div className="sp-warn">This piece was not sold through an HKP memo, or it is still in your stock. It can't go on this credit note.</div>
              <div className="t16">What to do</div>
              <ol>
                <li>Make its credit note directly in your billing software.</li>
                <li>If the piece is back with you, use <b>Take it back</b> to restock it.</li>
              </ol>
              <div className="sp-btns">
                <button type="button" className="sp-btn" onClick={() => history.push("/sales/take-back")}>Take it back</button>
                <button type="button" className="sp-btn is-pri" onClick={() => { setProblem(null); openScan(); }}>Keep scanning</button>
              </div>
            </>
          ) : problem.kind === "otherParty" ? (
            <>
              <h2>This piece belongs to another party</h2>
              <p className="t16" style={{ fontWeight: 400, margin: 0 }}>
                It was sold to <b>{problem.party}</b>. One credit note is for one party. Start a new credit note for {problem.party} with this piece.
              </p>
              <div className="sp-btns">
                <button type="button" className="sp-btn" onClick={() => setProblem(null)}>Skip</button>
                <button type="button" className="sp-btn is-pri" onClick={() => { const q = problem.query; setProblem(null); history.push(`/sales/credit/new?add=${encodeURIComponent(q)}`); }}>New credit note</button>
              </div>
            </>
          ) : null}
        </Sheet>
      ) : null}

      <ZohoVoidSheet memo={memo} zoho={zoho} />

      {menuOpen ? (
        <Sheet label="More actions" onClose={() => setMenuOpen(false)}>
          <div className="sp-menu">
            <button type="button" onClick={() => { setMenuOpen(false); setPreview(true); }}>Preview / print memo</button>
            {zoho.hasDraft && canWrite ? (
              <button type="button" onClick={() => { setMenuOpen(false); zoho.openVoid(""); }}>Void Zoho draft</button>
            ) : null}
            {zoho.on && !zoho.held ? (
              <button type="button" onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}>Billed outside Zoho: add invoice number</button>
            ) : null}
            {canWrite && (draft || ready) && !zoho.pushing ? (
              <button type="button" className="is-danger"
                onClick={zoho.hasDraft ? () => { setMenuOpen(false); zoho.openVoid("cancel"); } : cancelMemo}>
                Cancel {isCredit ? "credit note" : isApproval ? "approval memo" : "memo"}
              </button>
            ) : null}
            {canWrite && done && !isCredit ? (
              <button type="button" onClick={() => history.push(`/sales/take-back?memo=${encodeURIComponent(memoId)}`)}>Wrong bill / reverse</button>
            ) : null}
          </div>
        </Sheet>
      ) : null}

      <AddPartyModal
        openModal={addPartyOpen}
        closeModal={() => setAddPartyOpen(false)}
        gstOptions={gstOptions}
        handleOnInputChangeFilters={handleOnInputChangeFilters}
        getAllData={(res: any) => {
          const created = partyFromConnect(res);
          setAddPartyOpen(false);
          if (created) void pickParty(created);
        }}
      />
      {preview && memo ? <SalesMemoDocument memo={memo} onClose={() => setPreview(false)} /> : null}
    </div>
  );
};

export default connect(null, { getOrganisation })(SalesPhoneMemo);
